import apiClient from "./apiService";
import environment from "../config/environment";

const getSecretHeaders = (extraHeaders = {}) => ({
	"X-Secret-Software-Key": environment.secretSoftwareKey,
	"Content-Type": "application/json",
	Accept: "application/json",
	...extraHeaders,
});

/**
 * Általános adatszolgáltatás API hívásokhoz
 */
export const dataService = {
	/**
	 * @returns {Promise}
	 */
	getPartners(body = {}) {
		return apiClient.post("/partners", body);
	},

	/**
	 * @returns {Promise}
	 */
	getShoesMedtypes() {
		return apiClient.get("options/shoes_med_types", { headers: getSecretHeaders() });
	},

	/**
	 * @returns {Promise}
	 */
	getAllOptions() {
		return apiClient.get("options/all_options", { headers: getSecretHeaders() });
	},

	/**
	 * @returns {Promise}
	 */
	getDoctors(body = {}) {
		return apiClient.post("/doctors", body);
	},

	/**
	 * TAJ alapján érvényesség keresése
	 * @param {object} body
	 * @returns {Promise}
	 */
	checkTaj(body = {}) {
		return apiClient.post("/check_taj", body);
	},

	/**
	 * Partner adatok mentése
	 * @param {object} partner
	 * @returns {Promise}
	 */
	savePartner(partner) {
		return apiClient.post("/partner", { partner: partner });
	},

	/**
	 * Rendelés adatok mentése
	 * @param {object} orderData
	 * @returns {Promise}
	 */
	saveOrder(orderData) {
		return apiClient.post("/order", orderData);
	},


	resetPrescriptions(hmeliorder_szam) {
		return apiClient.post("/reset_prescriptions", { hmeliorder_szam: hmeliorder_szam });
	},

	/**
	 * Rendelések lekérése
	 * @param {object} params
	 * @returns {Promise}
	 */
	getOrders(body = {}) {
		return apiClient.post("/orders", body);
	},

	/**
	 * Rendelés lekérése azonosító alapján
	 * @param {string|number} hmeliorder_szam
	 * @param {string|number} partner_szam
	 * @returns {Promise}
	 */
	getOrder(hmeliorder_szam, partner_szam) {
		return apiClient.get(`/order/${hmeliorder_szam}/${partner_szam}`);
	},

	getNextPrefixNr(depot_prefix) {
		return apiClient.get(`/next_specific_id/${depot_prefix}`);
	},

	/**
	 * Rendelés frissítése
	 * @param {string|number} orderId
	 * @param {object} orderData
	 * @returns {Promise}
	 */
	updateOrder(orderId, orderData) {
		return apiClient.put(`/orders/${orderId}`, orderData);
	},

	/**
	 * Lokációk lekérése
	 * @returns {Promise}
	 */
	async getLocations() {
		return apiClient.get("/measurement_locations");
	},

	/**
	 * Városok lekérése
	 * @returns {Promise}
	 */
	getCities() {
		return apiClient.get("/cities");
	},

	/**
	 * Vény beküldése
	 * @returns {Promise}
	 */
	savePrescription(prescriptionData) {
		return apiClient.post("/prescription", prescriptionData);
	},

	/**
	 * Vény törlése
	 * @returns {Promise}
	 */

	deletePrescription(prescriptionData) {
		return apiClient.post("/remove_prescription", prescriptionData);
	},

	/**
	 * Modell lekérdezése rendeléshez
	 * @returns {Promise}
	 */
	getShoeModelForOrder(body = {}) {
		return apiClient.post("/shoe_model_for_order", body);
	},

	/**
	 * Kép feltöltése
	 * @param {object} imageData
	 * @returns {Promise}
	 */
	uploadImage(imageData) {
		return apiClient.post("/image", imageData);
	},

	/**
	 * Kép törlése
	 * @param {object} imageData
	 * @returns {Promise}
	 */
    deleteImage: async (data) => {
        return await apiClient.post(`/remove_image`, data);
    },

	/**
	 * Adatbázis kép lekérése
	 * @param {object} imageData
	 * @returns {Promise}
	 */
    getImage: (data) => {
        return apiClient.get(`/image`, { params: data });
    },

	/**
	 * Szabad projektek lekérdezése
	 * @returns {Promise}
	 */
	getAvailableProjects() {
		return apiClient.post("/available_projects");
	},

	/**
	 * Riport lekérdezése
	 * @returns {Promise}
	 */
	getReport(data) {
		return apiClient.post("/report", data);
	},
	
	/**
	 * Új orvos hozzáadása
	 * @param {object} doctorData
	 * @returns {Promise}
	 */
	addDoctor(doctorData) {
		return apiClient.post("/doctor", doctorData);
	},

	/**
	 * Divitemek lekérdezése
	 * @param {object} order
	 * @returns {Promise}
	 */
	getDivitems(order) {
		return apiClient.post("/divitems", order);
	},

	/**
	 * Általános GET hívás (pl. admin API-khoz)
	 * @param {string} endpoint
	 * @returns {Promise}
	 */
	get(endpoint) {
		return apiClient.get(endpoint, { headers: getSecretHeaders() });
	},

	/**
	 * Általános POST hívás (pl. admin API-khoz)
	 * @param {string} endpoint
	 * @param {object} data
	 * @returns {Promise}
	 */
	post(endpoint, data = {}) {
		return apiClient.post(endpoint, data, { headers: getSecretHeaders() });
	},

	/**
	 * Általános PUT hívás (pl. admin API-khoz)
	 * @param {string} endpoint
	 * @param {object} data
	 * @returns {Promise}
	 */
	put(endpoint, data = {}) {
		return apiClient.put(endpoint, data, { headers: getSecretHeaders() });
	},

	/**
	 * Általános DELETE hívás (pl. admin API-khoz)
	 * @param {string} endpoint
	 * @returns {Promise}
	 */
	delete(endpoint) {
		return apiClient.delete(endpoint, { headers: getSecretHeaders() });
	},

	/**
	 * Számla mentése
	 * @param {object} invoiceData
	 * @returns {Promise}
	 */
	saveInvoice(invoiceData) {
		return apiClient.post("/invoice", invoiceData);
	},
	/**
	 * BNO kódok
	 * @param {object} invoiceData
	 * @returns {Promise}
	 */
	getBNOs(invoiceData) {
		return apiClient.post("/bnos", invoiceData);
	},
	/**
	 * User preferenciák
	 * @param {object} invoiceData
	 * @returns {Promise}
	 */
	getUser_prefs(invoiceData) {
		return apiClient.post("/user_prefs", invoiceData);
	}

};

export default dataService;
